import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Divider,
  Container,
  Chip,
  TablePagination,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { getAssociateLedgerReport } from '../../services/associate';
import { getProfileDetail } from '../../services/profile';
import RefreshButton from '../../components/RefreshButton/RefreshButton';

const MyHistory = ({ associateId: propAssociateId }) => {
  const history = useHistory();
  const [loading, setLoading] = useState(!propAssociateId);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [associateId, setAssociateId] = useState(propAssociateId);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    itemsPerPage: 10,
    currentPage: 1
  });

  useEffect(() => {
    if (!associateId && !propAssociateId) {
      const fetchProfile = async () => {
        setLoading(true);
        try {
          const body = await getProfileDetail();
          console.log('Profile response in MyHistory:', body);
          const data = body?.data || (body?.success ? body.data : body);

          if (data) {
            // Priority: hex string ID usually in _id or id
            const id = data._id || data.id || data.associateId || data.associate?._id || data.user?._id;
            if (id) {
              const strId = String(id);
              console.log('Extracted Associate ID:', strId);
              setAssociateId(strId);
            } else {
              setError("Associate ID not found in profile");
              setLoading(false);
            }
          } else {
            setError("Profile data is empty");
            setLoading(false);
          }
        } catch (err) {
          console.error('Fetch profile error:', err);
          setError("Failed to identify associate session: " + err.message);
          setLoading(false);
        }
      };
      fetchProfile();
    }
  }, [associateId, propAssociateId]);

  const fetchHistory = useCallback(async () => {
    if (!associateId) return;

    setLoading(true);
    setError(null);
    try {
      console.log('Fetching history for ID:', associateId);
      const params = {
        page: page + 1,
        limit: rowsPerPage
      };
      const result = await getAssociateLedgerReport(associateId, params);
      console.log('History API result:', result);

      if (result.success || result.status || Array.isArray(result.data)) {
        setData(result.data || []);
        if (result.pagination) {
          setPagination(result.pagination);
        } else {
          setPagination(prev => ({
            ...prev,
            totalItems: result.totalCount || result.data?.length || 0
          }));
        }
      } else {
        throw new Error(result.message || 'Failed to fetch payment history');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching associate history:', err);
    } finally {
      setLoading(false);
    }
  }, [associateId, page, rowsPerPage]);

  useEffect(() => {
    if (associateId) {
      fetchHistory();
    }
  }, [associateId, fetchHistory]);

  const formatAmount = (amount) => {
    if (amount === undefined || amount === null) return "₹0.00";
    return Number(amount).toLocaleString('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totals = useMemo(() => {
    return data.reduce((acc, curr) => {
      if (curr.transactionType === 'add') {
        acc.credit += (Number(curr.amount) || 0);
      } else {
        acc.debit += (Number(curr.amount) || 0);
      }
      acc.balance = acc.credit - acc.debit;
      return acc;
    }, { credit: 0, debit: 0, balance: 0 });
  }, [data]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedData = data;

  if (loading && !data.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data.length) { // Only show full error if no data is loaded yet
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => history.goBack()} color="primary" sx={{ bgcolor: 'white', boxShadow: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon fontSize="large" sx={{ color: 'primary.main' }} />
              Payment History
            </Typography>
          </Box>
        </Box>
        <RefreshButton onClick={fetchHistory} loading={loading} />
      </Box>

      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 10px 30px rgba(0,0,0,0.05)' }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
            Associate Payment History
          </Typography>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Total Credit</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'success.main' }}>
                {formatAmount(totals.credit)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Total Debit</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'error.main' }}>
                {formatAmount(totals.debit)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Balance Amount</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
                {formatAmount(totals.balance)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead sx={{ backgroundColor: 'grey.100' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', py: 2 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Voucher ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Reference</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Mode</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>TDS</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right' }}>Balance</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 10 }}>
                    <Typography color="text.secondary">No transaction history found.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row) => (
                  <TableRow key={row._id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(row.createdAt)}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>#{row.voucherId || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.transactionType === 'add' ? 'CREDIT' : 'DEBIT'}
                        size="small"
                        sx={{
                          fontWeight: 800,
                          fontSize: '0.65rem',
                          backgroundColor: row.transactionType === 'add' ? 'success.light' : 'error.light',
                          color: row.transactionType === 'add' ? 'success.dark' : 'error.dark',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {row.bookingIdNo ? (
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>Bk #{row.bookingIdNo}</Typography>
                          <Typography variant="caption" color="text.secondary">{row.inventoryName || '-'}</Typography>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>{row.paymentMode || 'Cash'}</Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', fontWeight: 700 }}>
                      {formatAmount(row.amount)}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', color: 'text.secondary' }}>
                      {row.tdsAmount > 0 ? formatAmount(row.tdsAmount) : '-'}
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right', fontWeight: 800, color: 'primary.dark' }}>
                      {formatAmount(row.newBalance)}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
                        {row.description || '-'}
                        {row.bankName && ` (${row.bankName})`}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {data.length > 0 && (
          <TablePagination

            component="div"
            count={pagination.totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ mt: 2 }}
          />
        )}
      </Paper>
    </Container>
  );
};

export default MyHistory;
